import { snapshot } from 'valtio';
import { getActiveMembers } from '../../members/api/members-api';
import {
  loadApplications,
  loadSingleApplication,
} from '../../applications/api/applications-api';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import applicationsService from '../services';
import AppOwnersStore from '../stores/app-owners-store';
import applicationOwnerService from '../services';
import { logger } from 'logging-utils';

export const editApplicationOwners = async () => {
  AppOwnersStore.isLoading = true;
  const { editAppOwnerRoleRequest } = snapshot(AppOwnersStore);

  const { appId, role, owners } = editAppOwnerRoleRequest;
  if (!appId || !role) {
    openSnackbar('Missing required parameters', {
      variant: 'error',
    });
    return;
  }
  try {
    const results = await applicationOwnerService.editApplicationOwners.execute(
      {
        appId,
        role,
        // striping __typename for when object returnd from Apollo Client cache
        owners: owners.map(o => {
          return {
            email: o.email,
            name: o.name,
          };
        }),
      },
    );

    if (results?.isUpdated) {
      fetchAssignedAppOwners();
      loadApplications({
        update: true,
        cache: false,
      });
      loadSingleApplication({ cache: false });
      openSnackbar('Application owners updated!', {
        variant: 'success',
      });
    }
  } catch (error) {
    openSnackbar('Faild to update selected application owners!', {
      variant: 'error',
    });
    logger.error(error);
  } finally {
    AppOwnersStore.isLoading = false;
  }
};

export const fetchAssignedAppOwners = async () => {
  AppOwnersStore.isLoading = true;
  try {
    const owners = await applicationsService.getApplicationsOwners.execute();
    if (owners) {
      const { displaySelectedAppOwners } = snapshot(AppOwnersStore);
      const assignedAppOwnersEmails = owners.map(o => o.email);
      AppOwnersStore.assignedAppOwners = owners;

      if (
        !displaySelectedAppOwners.every(o =>
          assignedAppOwnersEmails.includes(o),
        )
      ) {
        const displaySelectedAppOwnersUpdated = displaySelectedAppOwners.filter(
          o => assignedAppOwnersEmails.includes(o),
        );
        AppOwnersStore.displaySelectedAppOwners = [
          ...displaySelectedAppOwnersUpdated,
        ];
      }
    }
  } finally {
    AppOwnersStore.isLoading = false;
  }
};

export const getActiveMembersSuggestions = async () => {
  const activeMembers = await getActiveMembers();
  if (activeMembers) {
    AppOwnersStore.activeMembers = activeMembers.map(member => {
      const { name, email } = member;
      return { name, email };
    });
  }
};

export const saveApplicationsOwners = async (close: () => void) => {
  AppOwnersStore.isLoading = true;
  const { assignAppOwnersRequest } = snapshot(AppOwnersStore);
  const { appIds, roles, owners } = assignAppOwnersRequest;

  try {
    const results = await applicationsService.saveApplicationsOwners.execute({
      appIds,
      roles,
      owners,
    });

    if (results?.isUpdated) {
      fetchAssignedAppOwners();
      loadApplications({
        update: true,
        cache: false,
      });
      loadSingleApplication({ cache: false });
      openSnackbar('Selected application owners assigned successfully!', {
        variant: 'success',
      });
      close();
      return;
    }
    openSnackbar('Faild to update selected application owners!', {
      variant: 'error',
    });
  } finally {
    AppOwnersStore.isLoading = false;
  }
};

export const removeAllAppOwners = async () => {
  const { assignAppOwnersRequest } = snapshot(AppOwnersStore);
  const { appIds } = assignAppOwnersRequest;
  const results = await applicationsService.removeAllAppOwners.execute({
    appIds,
  });
  if (results?.isRemoved) {
    openSnackbar('Application owners were successfully removed!', {
      variant: 'success',
    });
    await fetchAssignedAppOwners();
    return;
  }
  openSnackbar('Faild to remove application owners!', {
    variant: 'error',
  });
};
